import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Lock, CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenError('No reset token provided.');
        setVerifying(false);
        return;
      }

      try {
        const response = await api.get(`/auth/verify-reset-token?token=${token}`);
        if (response.data.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setTokenError(
            response.data.reason === 'expired'
              ? 'This reset link has expired. Please request a new one.'
              : 'This reset link is invalid. Please request a new one.'
          );
        }
      } catch (error) {
        setTokenValid(false);
        setTokenError('Unable to verify reset link. Please try again.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password. Please try again.';
      toast.error(message);
      
      // If token is now invalid, show error state
      if (error.response?.status === 400) {
        setTokenValid(false);
        setTokenError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50 p-4">
        <div className="max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50 p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img 
              src="/smarter-hoops-logo.png" 
              alt="Smarter Hoops" 
              className="h-24 mx-auto mb-4"
            />
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-md p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Reset Link</h2>
              <p className="text-slate-600 mb-6">{tokenError}</p>
              
              <Link to="/">
                <Button
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50 p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img 
              src="/smarter-hoops-logo.png" 
              alt="Smarter Hoops" 
              className="h-24 mx-auto mb-4"
            />
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-md p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Password Reset Complete</h2>
              <p className="text-slate-600 mb-6">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              
              <Link to="/">
                <Button
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide"
                  data-testid="go-to-login-btn"
                >
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50 p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="/smarter-hoops-logo.png" 
            alt="Smarter Hoops" 
            className="h-24 mx-auto mb-4"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold font-heading uppercase tracking-tight">
              Set New Password
            </h2>
            <p className="text-slate-600 text-sm mt-2">
              Enter your new password below.
            </p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new-password" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                New Password
              </Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
                data-testid="new-password-input"
                className="h-12 bg-white border-slate-200"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <Label htmlFor="confirm-password" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                Confirm Password
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="confirm-password-input"
                className="h-12 bg-white border-slate-200"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-password-btn"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide shadow-sm hover:shadow-md transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
